// ProofImagePicker — Phase 18 payment proof upload widget.
//
// ════════════════════════════════════════════════════════════════════
// WHAT IT DOES
// ════════════════════════════════════════════════════════════════════
//
// Renders a single file input + small preview. When the user picks
// an image we:
//
//   1. Validate type (image/png|jpeg|webp only) + size (≤ 5 MB).
//   2. Decode the file via <canvas> and re-export as image/jpeg @ 0.85.
//      This step DESTROYS polyglots — any HTML / JS / ZIP / PDF
//      attempt at hiding inside the upload is dropped because we
//      only read pixel data out of the canvas. The server runs a
//      paired sharp re-encode for defense-in-depth.
//   3. Hand the cleaned Blob to the parent via the onFile callback.
//
// The parent component packages the Blob into a FormData and POSTs
// it to the appropriate /proof or /payout-proof endpoint. Different
// surfaces (player wallet recharge, agent recharge form, approver
// mark-paid modal) all use this same widget — picker pure UI,
// parent owns the upload path.
//
// ════════════════════════════════════════════════════════════════════
// PROPS
// ════════════════════════════════════════════════════════════════════
//
//   onFile      (blob: Blob, dataUrl: string) => void
//                Called when the user picks a valid image and the
//                canvas re-encode succeeds.
//   onClear     () => void   Optional. Clears the preview.
//   label       string       Visible label above the picker. Default:
//                            "Upload screenshot of the payment for fast action."
//   disabled    boolean      Greys out the input.
//
// The component is fully controlled — it doesn't hold the final
// upload state. Errors during decode are surfaced via `onError`.

import { useRef, useState } from 'react';
import Icon from './Icon';
import { useT } from '../lib/i18n';

type Props = {
  onFile: (blob: Blob, dataUrl: string) => void;
  onClear?: () => void;
  onError?: (msg: string) => void;
  label?: string;
  disabled?: boolean;
  hint?: string;
  // 2026-05-21 — `prominent` swaps the chrome to a big eye-catching
  // camera button so the action reads as the primary CTA on the
  // screen (player recharge Step 3). The compact variant stays the
  // default so every existing consumer keeps its tight inline picker.
  variant?: 'compact' | 'prominent';
  // Override copy for the action chip (only used in `prominent`).
  // Defaults to "Add Screenshot" to lean into action language.
  buttonText?: string;
};

const MAX_BYTES = 5 * 1024 * 1024;          // 5 MB raw upload cap
const MAX_DIM   = 1600;                     // px — match server's PROOF_MAX_DIM
const QUALITY   = 0.85;                     // JPEG quality on canvas export
const ALLOWED   = new Set(['image/png', 'image/jpeg', 'image/webp']);

// Public re-exports for callers that build their own compact pickers
// (e.g. PayoutProofCell's inline "+ Upload" chip inside narrow card
// columns) but still want the SAME validation contract as the full
// widget. Keeps the limits in one place.
export const PROOF_PICKER_MAX_BYTES = MAX_BYTES;
export const PROOF_PICKER_ALLOWED_MIME: ReadonlySet<string> = ALLOWED;

export default function ProofImagePicker({
  onFile, onClear, onError,
  label,
  disabled = false, hint,
  variant = 'compact',
  buttonText,
}: Props) {
  const t = useT();
  // Default label / button copy comes from the i18n catalogue so callers
  // that don't pass an explicit `label` still get a localized string
  // (and the screenshot upload card in Wallet flips language with the
  // rest of the UI). Explicit props from callers still win — that's how
  // the player-recharge Step 3 card overrides the copy where needed.
  const resolvedLabel = label ?? t('wallet.pending.upload.label');
  const resolvedButton = buttonText
    ?? t(variant === 'prominent' ? 'proofPicker.button.prominent' : 'proofPicker.button.compact');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';                    // allow re-picking the same file
    if (!file) return;
    if (!ALLOWED.has(file.type)) {
      onError?.(t('proofPicker.error.mime'));
      return;
    }
    if (file.size > MAX_BYTES) {
      onError?.(t('proofPicker.error.size'));
      return;
    }
    setBusy(true);
    try {
      const cleaned = await reencodeViaCanvas(file);
      const dataUrl = await blobToDataUrl(cleaned);
      setPreviewUrl(dataUrl);
      onFile(cleaned, dataUrl);
    } catch (err) {
      onError?.((err as Error).message || t('proofPicker.error.processing'));
    } finally {
      setBusy(false);
    }
  }

  function clear() {
    setPreviewUrl(null);
    onClear?.();
  }

  // Layout contract:
  //   • Outer is `block w-full max-w-full min-w-0` — the picker takes
  //     EXACTLY the width its parent column gives it (never more), so a
  //     narrow History card on a 360-px phone doesn't get a horizontally
  //     overflowing pill that bleeds into the next row's content.
  //   • Label uses `flex w-full` with `whitespace-normal break-words text-center`
  //     so the text wraps to two lines on tight columns instead of
  //     forcing horizontal scroll.
  //   • Preview <img> keeps `max-w-full` and aspect-ratio so portrait
  //     screenshots can't blow out the column.
  return (
    <div className="space-y-1.5 block w-full max-w-full min-w-0">
      <div className="text-[10.5px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/70 break-words">
        {resolvedLabel}
      </div>
      {previewUrl ? (
        <div className="relative inline-block max-w-full">
          <img src={previewUrl} alt={t('proofPicker.previewAlt')}
               className="max-h-40 max-w-full rounded-xl ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40" />
          <button type="button" onClick={clear} disabled={disabled || busy}
                  className="absolute -top-2 -right-2 grid place-items-center w-6 h-6 rounded-full text-white
                             bg-rose-600 hover:bg-rose-500 active:bg-rose-700 shadow-[0_2px_6px_rgba(244,63,94,0.5)]
                             disabled:opacity-40">
            ×
          </button>
        </div>
      ) : variant === 'prominent' ? (
        // Loud emerald CTA — fills the column, big camera icon, white
        // text. Used on the recharge Step 3 card where the screenshot
        // is the primary action we want the player to take.
        <label className={`relative flex w-full max-w-full items-center justify-center gap-2.5 px-4 py-4 rounded-2xl
                           font-extrabold text-[14px] tracking-wider uppercase
                           transition cursor-pointer text-center whitespace-normal break-words leading-tight
                           border-2 shadow-lg active:scale-[.98] select-none
            ${disabled || busy
              ? 'bg-arena-200 text-arena-400 border-arena-300 shadow-none cursor-not-allowed'
              : 'text-white border-white/20 shadow-emerald-500/40 \
                 bg-gradient-to-b from-emerald-400 via-emerald-500 to-emerald-600 \
                 hover:from-emerald-300 hover:via-emerald-400 hover:to-emerald-500'}`}>
          {busy
            ? <><Icon name="progress_activity" size={22} className="animate-spin" /> {t('proofPicker.processing')}</>
            : <><Icon name="photo_camera" size={26} filled /> {resolvedButton}</>}
          <input type="file" accept="image/jpeg,image/png,image/webp"
                 ref={inputRef} onChange={pick}
                 disabled={disabled || busy} className="hidden" />
        </label>
      ) : (
        <label className={`flex w-full max-w-full items-center justify-center gap-2 px-3 py-2 rounded-xl text-[12.5px] font-semibold ring-1 ring-inset transition cursor-pointer text-center whitespace-normal break-words leading-tight
            ${disabled || busy
              ? 'bg-arena-100 text-arena-400 ring-arena-200/50 cursor-not-allowed'
              : 'bg-white dark:bg-arena-700/40 text-arena-700 dark:text-neon-200/85 ring-arena-200/60 dark:ring-arena-600/40 hover:bg-arena-50 dark:hover:bg-arena-600/40'}`}>
          <span className="inline-flex items-center gap-1.5">
            {busy
              ? <><Icon name="progress_activity" size={14} className="animate-spin" /> {t('proofPicker.processing')}</>
              : <><Icon name="photo_camera" size={14} filled /> {resolvedButton}</>}
          </span>
          <input type="file" accept="image/jpeg,image/png,image/webp"
                 ref={inputRef} onChange={pick}
                 disabled={disabled || busy} className="hidden" />
        </label>
      )}
      {hint && (
        <div className="text-[10.5px] text-arena-500 dark:text-neon-200/55 leading-tight break-words">
          {hint}
        </div>
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────

/**
 * Decode a File via <canvas> and re-export as image/jpeg.
 *
 * Canvas re-export is the browser-side defense layer: the canvas
 * 2D context reads PIXEL DATA ONLY out of the source image, then
 * we serialise it back to JPEG. Any non-pixel payload (EXIF, ICC,
 * polyglot HTML/PDF/ZIP, embedded scripts, malicious metadata) is
 * left behind. Downscales to MAX_DIM to bound payload size.
 *
 * The output Blob is always image/jpeg with type 'image/jpeg', no
 * matter what the source was. The server's sharp pass re-encodes
 * again (defense-in-depth) so the end-result on disk is a
 * twice-laundered JPEG that cannot carry executable content.
 */
// Exported so compact pickers (e.g. PayoutProofCell's inline "+ Upload"
// chip) can reuse the same canvas-laundering pipeline without dragging
// in the full preview UI.
export async function reencodeProofImageViaCanvas(file: File): Promise<Blob> {
  return reencodeViaCanvas(file);
}

async function reencodeViaCanvas(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  let { width, height } = bitmap;
  // Cap longest edge to MAX_DIM, preserve aspect ratio.
  if (width > MAX_DIM || height > MAX_DIM) {
    const ratio = Math.min(MAX_DIM / width, MAX_DIM / height);
    width  = Math.round(width  * ratio);
    height = Math.round(height * ratio);
  }
  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Browser cannot prepare image (no canvas context)');
  ctx.fillStyle = '#fff';                            // flatten any transparency to white
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close?.();
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => b ? resolve(b) : reject(new Error('Browser image encoder failed')),
      'image/jpeg', QUALITY,
    );
  });
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error || new Error('Read failed'));
    reader.readAsDataURL(blob);
  });
}
